import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, ArrowRight, CheckCircle2, Warehouse, Loader2 } from "lucide-react";
import { warehouses, companyFacts } from "@/data/content";
import useSeo from "@/hooks/useSeo";

const FORMSPREE_ENDPOINT = process.env.REACT_APP_FORMSPREE_ENDPOINT;

export default function Contact() {
    useSeo({
        title: "Contact — Sector 74A, Gurgaon",
        description:
            "Reach Beeta Logistics — info@beetalogistics.com · +91 99580 18358. Corporate office at 1816, Tower-4, DLF Corporate Greens, Sector 74-A, Gurgaon. 28 branches and 6+ warehouses across India.",
        path: "/contact",
    });
    const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.name || !form.email || !form.message) {
            setError("Name, email and message are required.");
            return;
        }
        if (!/^\S+@\S+\.\S+$/.test(form.email)) {
            setError("Please enter a valid email address.");
            return;
        }
        if (!FORMSPREE_ENDPOINT) {
            setError("Form is not configured. Please email info@beetalogistics.com directly.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(
                FORMSPREE_ENDPOINT,
                {
                    name: form.name,
                    email: form.email,
                    company: form.company || "—",
                    message: form.message,
                    _subject: `New website enquiry — ${form.name}`,
                },
                { headers: { Accept: "application/json" } },
            );
            setSubmitted(true);
        } catch (err) {
            const data = err?.response?.data;
            let msg = "Could not submit the form. Please email info@beetalogistics.com directly.";
            if (typeof data?.error === "string") {
                msg = data.error;
            } else if (Array.isArray(data?.errors) && data.errors.length) {
                msg = data.errors.map((e) => e.message).join(" · ");
            }
            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page" className="bg-white">
            <section className="relative pt-16 pb-20 bg-slate-50 border-b border-slate-200 overflow-hidden">
                <div className="absolute inset-0 bg-grid-slate opacity-40" />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-5">
                        Contact
                    </div>
                    <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-black text-slate-900 tracking-tighter leading-[1.08] sm:leading-[0.95] max-w-4xl">
                        Let&apos;s plan your next move.
                    </h1>
                    <p className="mt-6 text-lg text-slate-600 max-w-2xl">
                        Tell us about your shipment, your SLAs or just say hi. The fastest
                        way to reach us is{" "}
                        <a href="mailto:info@beetalogistics.com" className="font-semibold text-blue-600 hover:underline">
                            info@beetalogistics.com
                        </a>{" "}
                        or a quick call to{" "}
                        <a href="tel:+919958018358" className="font-semibold text-blue-600 hover:underline">
                            +91 99580 18358
                        </a>
                        .
                    </p>
                </div>
            </section>

            <section className="py-20 sm:py-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
                    {/* Info */}
                    <div className="lg:col-span-5 space-y-4">
                        {[
                            {
                                icon: MapPin,
                                label: "Corporate Office",
                                value: companyFacts.headOffice,
                            },
                            {
                                icon: Phone,
                                label: "Phone",
                                value: companyFacts.phone,
                            },
                            {
                                icon: Mail,
                                label: "Email",
                                value: companyFacts.email,
                            },
                            {
                                icon: Clock,
                                label: "Hours",
                                value: "Mon–Sat · 09:00 – 19:00 IST\nControl tower · 24×7",
                            },
                        ].map((c) => {
                            const Icon = c.icon;
                            return (
                                <div
                                    key={c.label}
                                    className="p-6 rounded-2xl border border-slate-200 bg-white flex gap-4 hover:shadow-lg transition-all"
                                >
                                    <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                                        <Icon className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">
                                            {c.label}
                                        </div>
                                        <div className="text-slate-800 whitespace-pre-line leading-relaxed">
                                            {c.value}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        <div className="rounded-2xl overflow-hidden border border-slate-200 aspect-[4/3]">
                            <iframe
                                title="Beeta Logistics — 1816, Tower-4, DLF Corporate Greens, Sector 74-A, Gurgaon"
                                src="https://www.google.com/maps?q=DLF+Corporate+Greens+Tower+4+Sector+74A+Gurgaon+122004&output=embed"
                                className="w-full h-full border-0"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                allowFullScreen
                                data-testid="contact-map-iframe"
                            />
                        </div>
                    </div>

                    {/* Form */}
                    <div className="lg:col-span-7">
                        {submitted ? (
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                data-testid="contact-success"
                                className="rounded-3xl border border-green-200 bg-green-50 p-10 md:p-14 text-center"
                            >
                                <div className="w-16 h-16 rounded-full bg-green-600 text-white mx-auto flex items-center justify-center mb-6">
                                    <CheckCircle2 className="w-8 h-8" />
                                </div>
                                <h2 className="font-display text-3xl font-black text-slate-900 tracking-tighter mb-3">
                                    Message received.
                                </h2>
                                <p className="text-slate-700 max-w-md mx-auto">
                                    Thanks {form.name.split(" ")[0]}. Your message has been delivered to
                                    our team. We&apos;ll get back to you shortly at{" "}
                                    <span className="font-semibold">{form.email}</span>. For anything
                                    urgent, please reach us directly at{" "}
                                    <a href="mailto:info@beetalogistics.com" className="font-semibold text-blue-600 hover:underline">
                                        info@beetalogistics.com
                                    </a>{" "}
                                    or call{" "}
                                    <a href="tel:+919958018358" className="font-semibold text-blue-600 hover:underline">
                                        +91 99580 18358
                                    </a>
                                    .
                                </p>
                                <button
                                    onClick={() => {
                                        setSubmitted(false);
                                        setForm({ name: "", email: "", company: "", message: "" });
                                    }}
                                    data-testid="contact-reset"
                                    className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-blue-600 transition-colors"
                                >
                                    Send another <ArrowRight className="w-4 h-4" />
                                </button>
                            </motion.div>
                        ) : (
                            <form
                                onSubmit={handleSubmit}
                                data-testid="contact-form"
                                className="rounded-3xl border border-slate-200 bg-white p-6 md:p-10 shadow-sm space-y-5"
                            >
                                <div className="grid md:grid-cols-2 gap-5">
                                    <CField
                                        id="name"
                                        label="Your name *"
                                        placeholder="Rahul Mehta"
                                        value={form.name}
                                        onChange={(v) => update("name", v)}
                                        testid="contact-name"
                                    />
                                    <CField
                                        id="email"
                                        type="email"
                                        label="Email *"
                                        placeholder="rahul@company.com"
                                        value={form.email}
                                        onChange={(v) => update("email", v)}
                                        testid="contact-email"
                                    />
                                </div>
                                <CField
                                    id="company"
                                    label="Company"
                                    placeholder="e.g. Brightline Industries"
                                    value={form.company}
                                    onChange={(v) => update("company", v)}
                                    testid="contact-company"
                                />
                                <div>
                                    <label htmlFor="message" className="block text-sm font-bold text-slate-900 mb-2">
                                        How can we help? *
                                    </label>
                                    <textarea
                                        id="message"
                                        data-testid="contact-message"
                                        rows={6}
                                        value={form.message}
                                        onChange={(e) => update("message", e.target.value)}
                                        placeholder="Tell us about your shipment, SLAs or current pain points."
                                        className="w-full px-4 py-3 rounded-xl border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                    />
                                </div>

                                {error && (
                                    <div
                                        data-testid="contact-error"
                                        className="rounded-xl bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm"
                                    >
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    data-testid="contact-submit"
                                    disabled={submitting}
                                    className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-slate-900 text-white font-bold hover:bg-blue-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {submitting ? (
                                        <>
                                            Sending… <Loader2 className="w-4 h-4 animate-spin" />
                                        </>
                                    ) : (
                                        <>
                                            Send message <ArrowRight className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            {/* WAREHOUSE LOCATIONS */}
            <section className="py-20 sm:py-24 bg-slate-50 border-t border-slate-200" data-testid="warehouses-section">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mb-12">
                        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-4">
                            Warehouse Network
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl font-black text-slate-900 tracking-tighter">
                            Six warehouses across India&apos;s industrial belts.
                        </h2>
                        <p className="mt-5 text-slate-600 leading-relaxed">
                            From the Gurgaon–Manesar–Bawal cluster in the north, through
                            Greater Noida and Faridabad, down to Sriperumbudur (Chennai)
                            and Chakan (Pune) — Beeta is positioned where our customers
                            manufacture.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {warehouses.map((w, i) => (
                            <motion.div
                                key={w.city}
                                initial={{ opacity: 0, y: 18 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.06 }}
                                className="p-6 rounded-2xl border border-slate-200 bg-white hover:shadow-lg transition-all"
                                data-testid={`warehouse-card-${i}`}
                            >
                                <div className="flex items-start gap-3 mb-3">
                                    <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                                        <Warehouse className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-blue-600 mb-1">
                                            Warehouse
                                        </div>
                                        <div className="font-display text-lg font-bold text-slate-900 leading-tight">
                                            {w.city}
                                        </div>
                                    </div>
                                </div>
                                <p className="text-sm text-slate-600 leading-relaxed pl-1">
                                    {w.address}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

function CField({ id, label, placeholder, value, onChange, type = "text", testid }) {
    return (
        <div>
            <label htmlFor={id} className="block text-sm font-bold text-slate-900 mb-2">
                {label}
            </label>
            <input
                id={id}
                data-testid={testid}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full px-4 py-3 rounded-xl border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            />
        </div>
    );
}
